import {
  getServiceRequestServiceId,
  INDIRECT_TAX_EXPOSURE_SERVICE_ID,
  TWorkflowExecution,
} from '@commenda/types';
import {
  IServiceRequest,
  ServiceRequestDetails,
  TaskGroupWithSLA,
  TaskWithSLA,
} from '@commenda/types/src/service-request/types';
import {
  Country,
  ServiceRequestStatus,
  ServiceSubcategory,
  TaskGroupStatus,
  TaskParty,
  TaskStatus,
  WorkflowExecutionStatus,
} from '@prisma/client';
import { IndirectTaxNavigationHelper } from '../indirect-tax-navigation';
import { NonTimezoneSensitiveDate } from '../non-time-zone-sensitive-date';

export type TaOnboardingServiceRequest = {
  serviceCompositeId: string;
  explicitTitle?: string;
};

export const TA_ONBOARDING_SERVICE_IDS: string[] = [
  'OB_TNA_US',
  'OB_TNA_IN',
  'OB_TNA_CA',
  'OB_TNA_GB',
  'OB_TNA_IE',
  'OB_TNA_SG',
];

export const COUNTRY_TO_TA_ONBOARDING_SERVICE_REQUEST: Partial<
  Record<Country, TaOnboardingServiceRequest>
> = {
  [Country.US]: { serviceCompositeId: 'OB_TNA_US' },
  [Country.IN]: { serviceCompositeId: 'OB_TNA_IN' },
  [Country.CA]: { serviceCompositeId: 'OB_TNA_CA' },
  [Country.GB]: { serviceCompositeId: 'OB_TNA_GB' },
  [Country.IE]: {
    serviceCompositeId: 'OB_TNA_IE',
    explicitTitle: 'Onboarding',
  },
  [Country.SG]: { serviceCompositeId: 'OB_TNA_SG' },
};

export const TA_SUPPORTED_COUNTRIES = Object.keys(
  COUNTRY_TO_TA_ONBOARDING_SERVICE_REQUEST
) as Country[];

// Aditya Ponappa's Agent user id, aditya+agent@commenda.io — prod only
export const TNA_DEFAULT_ASSIGNED_AGENT_FIRM_USER_ID =
  process.env.NEXT_PUBLIC_CLIENT_ENV === 'production'
    ? '263e17be-e404-4c5f-977a-72d58576e579'
    : undefined;

export class ServiceRequestModel {
  /**
   * Returns the exposure-dashboard URL when the SR is for the exposure
   * service, else null. Single source of truth for this routing rule — call sites
   * fall through with `?? defaultLink`. The exposure card on that page owns
   * the per-region "Register" CTA, so we don't deep-link further.
   */
  static getIndirectTaxExposureLink(
    serviceRequest: Parameters<typeof getServiceRequestServiceId>[0],
    corporationId: number | string | null | undefined
  ): string | null {
    if (
      getServiceRequestServiceId(serviceRequest) ===
        INDIRECT_TAX_EXPOSURE_SERVICE_ID &&
      corporationId
    ) {
      return `/indirect-tax/${corporationId}/exposure`;
    }
    return null;
  }

  static isTaxAndAccountingOnboarding(
    serviceRequest: Pick<IServiceRequest, 'commendaService'>
  ): boolean {
    if (serviceRequest.commendaService) {
      return (
        serviceRequest.commendaService.subcategory ===
        ServiceSubcategory.TNA_ONBOARDING
      );
    }
    return TA_ONBOARDING_SERVICE_IDS.includes(
      getServiceRequestServiceId(serviceRequest) ?? ''
    );
  }

  /**
   * Destination app URL after a product onboarding completes. Returns null for
   * service types that don't have a post-onboarding landing, so callers
   * fall through without redirecting. Add a branch per product as they gain
   * onboarding flows.
   */
  static getPostOnboardingRedirect(
    serviceRequest:
      | Pick<IServiceRequest, 'commendaService'>
      | undefined,
    corporationId?: number | string
  ): string | null {
    if (
      serviceRequest &&
      ServiceRequestModel.isTaxAndAccountingOnboarding(serviceRequest)
    ) {
      return corporationId
        ? `/tax-and-reporting?corporationId=${corporationId}`
        : '/tax-and-reporting';
    }
    return null;
  }

  /**
   * Enterprise (company-facing) link to a FILING detail page, keyed by the
   * sales-tax-api `filing_id`.
   */
  static getCompanyFilingLink(
    filingId: string,
    corporationId: number | string
  ): string {
    return IndirectTaxNavigationHelper.buildEntityPath(
      Number(corporationId),
      `filings/${filingId}`
    );
  }

  /** Enterprise (company-facing) link to a REGISTRATION detail page. */
  static getCompanyRegistrationLink(
    registrationId: string,
    corporationId: number | string
  ): string {
    return IndirectTaxNavigationHelper.buildEntityPath(
      Number(corporationId),
      `registrations/${registrationId}`
    );
  }

  /** Enterprise (company-facing) link to the FILINGS LIST for an entity. */
  static getCompanyFilingsListLink(corporationId: number | string): string {
    return IndirectTaxNavigationHelper.buildEntityPath(
      Number(corporationId),
      'filings'
    );
  }

  /**
   * Agent-app link to a FILING detail page: the agent surface nests under the
   * client (corporation) id, so the shape is different from the company app.
   * The indirect-tax surface is the live agent surface, so this is not gated.
   */
  static getAgentFilingLink(
    filingId: string,
    corporationId: number | string
  ): string {
    return `/clients/${corporationId}/indirect-tax/filings/${filingId}`;
  }

  /**
   * Agent-app link to a REGISTRATION detail page. Like the filing link, the
   * indirect-tax surface is the live agent surface, so this is not gated.
   */
  static getAgentRegistrationLink(
    registrationId: string,
    corporationId: number | string
  ): string {
    return `/clients/${corporationId}/indirect-tax/registrations/${registrationId}`;
  }

  /**
   * Standardized display name for a service request.
   * Reads the BE-computed `displayName` from the API response.
   * Falls back to `title`.
   */
  static getDisplayName(
    serviceRequest:
      | {
          displayName?: string;
          title?: string;
        }
      | null
      | undefined
  ): string {
    if (!serviceRequest) return 'N/A';
    return (
      serviceRequest.displayName ?? serviceRequest.title ?? 'N/A'
    );
  }

  static SERVICE_REQUEST_FILTER_COUNTRIES: Country[] = [
    Country.IN,
    Country.US,
    Country.CA,
    Country.AE,
    Country.GB,
    Country.IE,
    Country.SG,
  ];


  static ServiceRequestTaskLabelConfig: Record<TaskStatus, string | null> = {
    [TaskStatus.BACKLOG]: null,
    [TaskStatus.TO_DO]: null,
    [TaskStatus.IN_PROGRESS]: 'Last edited: ',
    [TaskStatus.COMPLETED]: 'Completed: ',
    [TaskStatus.CANCELED]: null,
    // Todo: STEP_REOPENED
    // Todo: STEP_SKIPPED
  };

  static supportedServicesForSkipAndUndo: string[] = [
    'EM_INC_IN_PVTLTD',
    'EM_INC_IN_LLP',
    'EM_INC_US_LLC',
    'EM_INC_US_CCORP',
    'EM_INC_CA',
    'EM_INC_AE',
    'EM_INC_GB',
    'EM_INC_IE',
    'EM_INC_SG',
  ];

  static getCurrentTaskGroups(
    filteredTaskGroups: TaskGroupWithSLA[]
  ): TaskGroupWithSLA[] | undefined {
    const inProgressTaskGroups = filteredTaskGroups?.filter(
      (taskGroup) => taskGroup.status === TaskGroupStatus.IN_PROGRESS
    );

    if (inProgressTaskGroups?.length) {
      return inProgressTaskGroups;
    }

    const notStartedTaskGroup = filteredTaskGroups?.find(
      (taskGroup) => taskGroup.status === TaskGroupStatus.NOT_STARTED
    );

    if (notStartedTaskGroup) {
      return [notStartedTaskGroup];
    }

    return [
      filteredTaskGroups?.find(
        (taskGroup) => taskGroup.legacyServiceRequestTaskGroupOrderNumber === 1
      ),
    ];
  }

  static isTaskEligibleForReopenOrSkip(
    task: TaskWithSLA,
    serviceRequest: ServiceRequestDetails
  ) {
    if (
      serviceRequest?.status === ServiceRequestStatus.NOT_STARTED ||
      !task ||
      !serviceRequest?.taskGroups ||
      serviceRequest?.workflowExecutionId
    )
      return false;

    const allTasksCompleted = this.isAllTasksCompleted(serviceRequest);

    if (allTasksCompleted) return false;

    const isNotStartedOrCompleted =
      task.status === TaskStatus.TO_DO ||
      task.status === TaskStatus.COMPLETED;

    if (!isNotStartedOrCompleted) return false;

    const currentTasks = this.getCurrentTasks(serviceRequest?.taskGroups);

    const currentTask = currentTasks?.find(
      (eachCurrentTask) => eachCurrentTask.id === task.id
    );

    if (currentTask?.id === task.id) return true;

    const previousTask = this.getPreviousCompletedTask(
      serviceRequest?.taskGroups
    );

    if (previousTask?.id === task.id) return true;

    return false;
  }

  static getCurrentTasks(
    taskGroups: TaskGroupWithSLA[]
  ): TaskWithSLA[] | null {
    if (!taskGroups?.length) return null;

    const allTasks = taskGroups.flatMap((group) => group.tasks);

    const statusesForCurrentTask: TaskStatus[] = [
      TaskStatus.IN_PROGRESS,
      TaskStatus.TO_DO,
    ];

    const currentTasks = allTasks.filter((task) =>
      statusesForCurrentTask.includes(task.status)
    );
    return currentTasks;
  }

  static getPreviousCompletedTask(
    taskGroups: TaskGroupWithSLA[]
  ): TaskWithSLA | null {
    if (!Array.isArray(taskGroups) || taskGroups.length === 0) {
      return null;
    }

    const allTasks = taskGroups.flatMap((group) => group.tasks ?? []);

    const completedTasks = allTasks.filter(
      (task) => task?.status === TaskStatus.COMPLETED
    );

    return completedTasks.at(-1) ?? null;
  }

  static isGenericServiceRequest(taskGroups: TaskGroupWithSLA[]) {
    return taskGroups?.length === 0;
  }

  static getTasksFilteredByRole(
    serviceRequest: ServiceRequestDetails,
    role: TaskParty
  ) {
    return (
      serviceRequest?.taskGroups?.flatMap((taskGroup: TaskGroupWithSLA) =>
        taskGroup.tasks.filter((task: TaskWithSLA) => task.pendingOn === role)
      ) || []
    );
  }

  static isServiceRequestEligibleToBeMarkedAsCompleted(params: {
    serviceRequest: ServiceRequestDetails;
    workflowExecution?: TWorkflowExecution;
  }) {
    const { serviceRequest, workflowExecution } = params;

    if (workflowExecution)
      return (
        workflowExecution.status === WorkflowExecutionStatus.COMPLETED &&
        serviceRequest.status !== ServiceRequestStatus.COMPLETED
      );

    return serviceRequest?.taskGroups?.every(
      (taskGroup) =>
        taskGroup.tasks.every(
          (task) => task.status === TaskStatus.COMPLETED
        ) && serviceRequest?.status !== ServiceRequestStatus.COMPLETED
    );
  }

  static getIconStatusColorBase(status: TaskStatus) {
    switch (status) {
      case TaskStatus.IN_PROGRESS:
        return 'bg-bg-warning';
      case TaskStatus.COMPLETED:
        return 'bg-bg-success';
      default:
        return 'bg-bg-tertiary';
    }
  }

  // We may want to extend this to include other statuses in the future, but for now we only change text color for completed tasks
  static getTaskLabelTextColor(status: TaskStatus) {
    switch (status) {
      case TaskStatus.COMPLETED:
        return 'text-text-success';
      default:
        return '';
    }
  }

  static getFormattedLastUpdatedAt(serviceRequest: ServiceRequestDetails) {
    return (
      NonTimezoneSensitiveDate.fromNativeDate(
        new Date(serviceRequest?.lastUpdatedAt)
      )?.format('MMM DD, YYYY') ?? ''
    );
  }

  static getServiceRequestCompanyName(serviceRequest: ServiceRequestDetails) {
    const n = serviceRequest?.corporation?.companyLegalName?.slice(0, 40);

    return n;
  }

  static isAllTasksCompleted(serviceRequest: ServiceRequestDetails) {
    return serviceRequest?.taskGroups?.every((taskGroup) =>
      taskGroup.tasks.every((task) => task.status === TaskStatus.COMPLETED)
    );
  }

  static shouldShowActiveStepStroke(role: TaskParty, task: TaskWithSLA) {
    return (
      role === task.pendingOn &&
      (task.status === TaskStatus.IN_PROGRESS ||
        task.status === TaskStatus.TO_DO)
    );
  }

  static shouldShowTaskCountBadge(
    taskGroup: TaskGroupWithSLA,
    currentTaskGroups: TaskGroupWithSLA[],
    role: TaskParty,
    serviceRequest: ServiceRequestDetails
  ) {
    if (!currentTaskGroups?.find((group) => group.id === taskGroup.id)) {
      return false;
    }

    const currentTasks = this.getCurrentTasks(serviceRequest?.taskGroups);

    const currentGroupTasks = currentTasks?.filter(
      (eachCurrentTask) => eachCurrentTask.taskGroupId === taskGroup.id
    );

    if (!currentGroupTasks?.length) {
      return false;
    }

    const taskCountByPendingOn = currentGroupTasks?.reduce(
      (acc, task) => {
        if (task.pendingOn) acc[task.pendingOn] = (acc[task.pendingOn] || 0) + 1;
        return acc;
      },
      {} as Record<TaskParty, number>
    );

    return taskCountByPendingOn[role] > 0;
  }
}
